// assets/product-card-quick-add.js
// Quick Add — Bottom Sheet showing the REAL PDP directly (no separate
// simplified quick-add form). Tapping the quick-add button opens a sheet
// at 85% viewport height on mobile with the actual product page content
// fetched via Shopify's Section Rendering API (`?section_id=main`) and
// injected in — including waiting for its stylesheets and re-executing
// its scripts, so gallery, variant switching, the real Add to Cart form
// and GoKwik's own init code all run exactly as they would on the real
// page. Swiping the drag handle up grows the SAME sheet to 100% height
// (no re-fetch needed, the content is already loaded). The URL updates via
// pushState so the address bar and back button behave correctly, and if a
// checkout button is still stuck disabled a few seconds after load
// (GoKwik's SDK not cleanly re-binding to a dynamically swapped product),
// it falls back to a normal page navigation automatically.
(function () {
  if (window.__housiousQuickAddInitialized) return;
  window.__housiousQuickAddInitialized = true;

  var DRAG_CLOSE_THRESHOLD = 90; // px dragged down before releasing closes the sheet
  var DRAG_EXPAND_THRESHOLD = 60; // px dragged up before releasing grows to 100%
  var STUCK_CHECKOUT_TIMEOUT = 3500; // ms before the safety-net navigation fallback fires

  class QuickAddSheet {
    constructor() {
      this.overlay = null;
      this.sheet = null;
      this.currentProductUrl = '#';
      this.currentHandle = null;
      this.expanded = false;
      this.expandTimer = null;
      this.popstateBound = false;
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add-btn]');
        if (btn) {
          e.preventDefault();
          this.open(btn.dataset.productHandle, btn.dataset.productUrl || '#');
        }
      });
    }

    buildSheetShell() {
      if (this.sheet) return;

      const overlay = document.createElement('div');
      overlay.className = 'qa-sheet-overlay';
      document.body.appendChild(overlay);
      this.overlay = overlay;

      const sheet = document.createElement('div');
      sheet.className = 'qa-sheet';
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.innerHTML = `
        <div class="qa-sheet__handle-row" data-qa-drag-handle>
          <span class="qa-sheet__handle"></span>
          <span class="qa-sheet__handle-hint">Swipe up for full screen</span>
        </div>
        <button type="button" class="qa-sheet__close" aria-label="Close">&times;</button>
        <div class="qa-sheet__content"></div>
      `;
      document.body.appendChild(sheet);
      this.sheet = sheet;

      overlay.addEventListener('click', () => this.close());
      sheet.querySelector('.qa-sheet__close').addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sheet.classList.contains('is-open')) this.close();
      });

      this.bindDragHandle(sheet.querySelector('[data-qa-drag-handle]'));
    }

    bindDragHandle(handle) {
      if (!handle) return;
      let startY = 0, currentDelta = 0, dragging = false;

      const onStart = (clientY) => {
        dragging = true;
        startY = clientY;
        currentDelta = 0;
        this.sheet.classList.add('is-dragging');
      };
      const onMove = (clientY) => {
        if (!dragging) return;
        currentDelta = clientY - startY;
        // Visual feedback only for a downward drag (closing gesture). An
        // upward drag (expand gesture) stays put visually until release so
        // the sheet doesn't fly off the top of the screen while dragging.
        this.sheet.style.transform = currentDelta > 0 ? 'translateY(' + currentDelta + 'px)' : '';
      };
      const onEnd = () => {
        if (!dragging) return;
        dragging = false;
        this.sheet.classList.remove('is-dragging');
        this.sheet.style.transform = '';
        if (!this.expanded && currentDelta <= -DRAG_EXPAND_THRESHOLD) {
          this.expand();
        } else if (currentDelta >= DRAG_CLOSE_THRESHOLD) {
          this.close();
        }
      };

      handle.addEventListener('touchstart', (e) => onStart(e.touches[0].clientY), { passive: true });
      handle.addEventListener('touchmove', (e) => onMove(e.touches[0].clientY), { passive: true });
      handle.addEventListener('touchend', onEnd);
      handle.addEventListener('mousedown', (e) => { e.preventDefault(); onStart(e.clientY); });
      window.addEventListener('mousemove', (e) => onMove(e.clientY));
      window.addEventListener('mouseup', onEnd);
    }

    async open(handle, productUrl) {
      this.buildSheetShell();
      this.currentHandle = handle;
      this.currentProductUrl = productUrl || '#';
      this.expanded = false;
      this.sheet.classList.remove('is-expanded');

      const contentEl = this.sheet.querySelector('.qa-sheet__content');
      contentEl.style.paddingBottom = '';
      contentEl.innerHTML =
        '<div class="qa-sheet__skeleton">' +
        '<div class="qa-sheet__skeleton-img"></div>' +
        '<div class="qa-sheet__skeleton-line" style="width:70%"></div>' +
        '<div class="qa-sheet__skeleton-line qa-sheet__skeleton-line--short"></div>' +
        '</div>';

      this.overlay.classList.add('is-open');
      this.sheet.classList.add('is-open');
      document.body.classList.add('qa-sheet-locked');

      history.pushState({ housiousQuickAdd: true, handle }, '', productUrl);
      if (!this.popstateBound) {
        window.addEventListener('popstate', () => this.handlePopstate());
        this.popstateBound = true;
      }

      try {
        const sep = productUrl.indexOf('?') > -1 ? '&' : '?';
        const res = await fetch(productUrl + sep + 'section_id=main');
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        const html = await res.text();
        await this.injectFullProduct(html, contentEl);
      } catch (err) {
        console.error('[HousiousQuickAdd] PDP load failed, falling back to navigation', err);
        window.location.href = productUrl;
      }
    }

    expand() {
      if (this.expanded || !this.sheet) return;
      this.expanded = true;
      this.sheet.classList.add('is-expanded');
    }

    close() {
      if (!this.sheet || !this.sheet.classList.contains('is-open')) return;
      // Pop the history entry pushed in open() rather than tearing the UI
      // down directly, so the address bar / back button stay consistent.
      history.back();
    }

    handlePopstate() {
      if (!this.sheet) return;
      this.expanded = false;
      clearTimeout(this.expandTimer);
      this.sheet.classList.remove('is-open', 'is-expanded');
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('qa-sheet-locked');
    }

    async injectFullProduct(html, contentEl) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot = doc.querySelector('#shopify-section-main') || doc.body;

      // Shopify's Section Rendering API often emits the section's own
      // <link>/<script> tags as siblings of the section wrapper rather than
      // nested inside it — pull them from the whole parsed document, not
      // just sectionRoot, before we touch the visible markup.
      const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
      const externalScripts = Array.from(doc.querySelectorAll('script[src]'));
      links.forEach((n) => n.remove());
      externalScripts.forEach((n) => n.remove());

      contentEl.innerHTML = '';
      contentEl.appendChild(sectionRoot.cloneNode(true));

      // Load any stylesheet this section needs that isn't already on the
      // page (product CSS, sticky-add-to-cart.css, etc.) — dedup by href,
      // and WAIT for each to actually finish loading. Skipping this wait
      // was the root cause of the layout looking off: later steps (like
      // measuring the sticky Add to Cart bar below) would run before the
      // real product styling had even applied.
      await Promise.all(links.map((link) => {
        const href = link.getAttribute('href');
        if (!href) return Promise.resolve();
        if (document.querySelector('link[href="' + CSS.escape(href) + '"]')) return Promise.resolve();
        return new Promise((resolve) => {
          const newLink = document.createElement('link');
          newLink.rel = 'stylesheet';
          newLink.href = href;
          newLink.onload = resolve;
          newLink.onerror = resolve;
          document.head.appendChild(newLink);
        });
      }));

      // Load external scripts (the GoKwik SDK, product JS, etc.) before
      // running inline setup code that likely depends on them — dedup by src.
      await Promise.all(externalScripts.map((script) => {
        const src = script.getAttribute('src');
        if (!src) return Promise.resolve();
        if (document.querySelector('script[src="' + CSS.escape(src) + '"]')) return Promise.resolve();
        return new Promise((resolve) => {
          const newScript = document.createElement('script');
          newScript.src = src;
          newScript.onload = resolve;
          newScript.onerror = resolve;
          document.body.appendChild(newScript);
        });
      }));

      // Re-run inline scripts from the fetched section — setting innerHTML
      // does not execute them, and this is what actually wires up gallery
      // switching, variant selection, the real Add to Cart form and
      // GoKwik's own init code against the freshly injected DOM. (Add to
      // Cart itself needs no extra wiring here — cart-drawer.js already
      // listens for [data-type="add-to-cart-form"] submits at the document
      // level, which covers this injected form automatically.)
      contentEl.querySelectorAll('script:not([src])').forEach((script) => {
        const newScript = document.createElement('script');
        newScript.textContent = script.textContent;
        script.replaceWith(newScript);
      });

      // The real PDP's sticky Add to Cart / Buy Now bar pins itself with
      // position:fixed;bottom:0. Because .qa-sheet uses a CSS transform for
      // its slide animation, it becomes the containing block for that fixed
      // bar (per the CSS spec), so it correctly stays pinned to the bottom
      // of THIS sheet rather than the real screen — but nothing reserves
      // space for it, so the last bit of scrollable content was hiding
      // behind it. Measure the real rendered bar (now that its stylesheet
      // has actually loaded, per the await above) and pad the scroll area
      // to match.
      requestAnimationFrame(() => {
        const stickyBar = contentEl.querySelector('.housious-cart-checkout-row');
        contentEl.style.paddingBottom = stickyBar ? (stickyBar.getBoundingClientRect().height + 16) + 'px' : '';
      });

      // Safety net: if the GoKwik Buy Now button in the injected PDP is
      // still stuck disabled a few seconds later — most likely GoKwik's SDK
      // not cleanly re-binding to a dynamically swapped product — fall back
      // to a normal page load rather than leaving the shopper with a dead
      // button. FIXED: the selector previously looked for an id containing
      // "checkout" and a `.cart-drawer__checkout-btn` class — neither
      // matches the real button (`#gokwik-buy-now`), so this safety net
      // was silently never firing.
      clearTimeout(this.expandTimer);
      this.expandTimer = setTimeout(() => {
        const stuckBtn = contentEl.querySelector('#gokwik-buy-now.disabled');
        if (stuckBtn) window.location.href = this.currentProductUrl;
      }, STUCK_CHECKOUT_TIMEOUT);
    }
  }

  document.addEventListener('DOMContentLoaded', () => new QuickAddSheet());
})();
